import { DataType, DataTypes } from "../../../metadata";
import { IColumn } from "../../interfaces";
import { Record } from "../Record/Record";
import { RecordFilter } from "./filtering/RecordFilter";
import { MemoryDataProvider } from "./MemoryDataProvider";
import { sort as fastSort } from 'fast-sort';

export class DataBuilder {
    private _data: { [key: string]: any }[] = [];
    private _dataProvider: MemoryDataProvider;
    private _primaryIdAttribute: string;
    constructor(primaryIdAttribute: string, dataSource: { [key: string]: any }[], dataProvider: MemoryDataProvider) {
        this._dataProvider = dataProvider;
        this._data = dataSource;
        this._primaryIdAttribute = primaryIdAttribute;
    }
    public setFulltextSearch(query?: string): DataBuilder {
        if (!query) {
            return this;
        }

        const lowerCaseQuery = query.toLowerCase();
        const columns = this._dataProvider.getColumns();
        this._data = this._data.filter(recordData => {
            for (const col of columns) {
                const formattedValue = new Record(recordData[this._primaryIdAttribute], this._dataProvider).getFormattedValue(col.name);
                if (formattedValue && formattedValue.toLowerCase().includes(lowerCaseQuery)) {
                    return true;
                }
            }
            return false;
        });

        return this;
    }
    public setSorting(sorting: ComponentFramework.PropertyHelper.DataSetApi.SortStatus[]): DataBuilder {
        if (sorting.length === 0) {
            return this;
        }
        this._data = fastSort(this._data).by(sorting.map(sort => {
            const column = this._dataProvider.getColumns().find(col => col.name === sort.name)!;
            const useFormattedValue = this._shouldUseFormattedValue(column.dataType as DataType);
            if (sort.sortDirection === 0) {
                return {
                    asc: u => useFormattedValue ? u : this._delegator(column, sort, u),
                    comparer: useFormattedValue ? (a, b) => this._compare(column.name, a, b) : undefined
                };
            }
            return {
                desc: u => useFormattedValue ? u : this._delegator(column, sort, u),
                comparer: useFormattedValue ? (a, b) => this._compare(column.name, a, b) : undefined
            };
        }));
        return this;
    }
    public setFiltering(filterExpression: ComponentFramework.PropertyHelper.DataSetApi.FilterExpression | null) {
        if (!filterExpression || filterExpression.conditions.length === 0) {
            return this;
        }
        this._data = this._data.filter(x => {
            const recordFilter = new RecordFilter(new Record(x[this._primaryIdAttribute], this._dataProvider), this._dataProvider);
            return recordFilter.validateConditions(filterExpression.filterOperator, filterExpression.conditions);
        });
        return this;
    }

    public getData() {
        return this._data;
    }
    private _shouldUseFormattedValue(dataType: DataType) {
        switch (dataType) {
            //sorted by getFormattedValue
            case DataTypes.LookupCustomer:
            //sorted by getFormattedValue
            case DataTypes.LookupOwner:
            //sorted by getFormattedValue
            case DataTypes.LookupRegarding:
            //sorted by getFormattedValue
            case DataTypes.LookupSimple:
            //sorted by getFormattedValue
            case DataTypes.OptionSet: {
                return true;
            }
            //For all other data types, we return false because it is not necessary to use formattedValue
            default: {
                return false;
            }
        }
    }

    private _delegator(column: IColumn, sort: ComponentFramework.PropertyHelper.DataSetApi.SortStatus, record: { [key: string]: any }) {
        let value = record[sort.name];
        if (value === null) {
            return undefined;
        }
        //invert the boolean values so true values are sort first
        if (column.dataType === DataTypes.TwoOptions) {
            return !value;
        }
        return value;
    }

    private _compare(columnName: string, a: any, b: any): number {
        const recordA = new Record(a[this._primaryIdAttribute], this._dataProvider);
        const recordB = new Record(b[this._primaryIdAttribute], this._dataProvider);
        const formattedValueA = recordA.getFormattedValue(columnName);
        const formattedValueB = recordB.getFormattedValue(columnName);
        if (!formattedValueA && !formattedValueB) {
            return 0;
        }
        if (!formattedValueA) {
            return 1;
        }
        if (!formattedValueB) {
            return -1;
        }
        return formattedValueA.localeCompare(formattedValueB);
    }
}